import { ROUTE_AVAILABILITY_GROUPS_DETAILS } from '@/constants/routes';
import { useEditEventTypeMutation } from '@/queries/eventTypes.queries';
import { useCreateResourceGroupMutation } from '@/queries/resourceGroups.queries';
import previewService from '@/services/preview.service';
import uuidService from '@/services/uuid.service';
import IEventType from '@/types/IEventType';
import IResourceGroup from '@/types/IResourceGroup';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

interface IArgs {
	resourceGroups: Array<IResourceGroup>;
	eventType: IEventType;
	isOpen: boolean;
	close: () => void;
}

export function useAddMemberModal(args: IArgs) {
	const { t } = useTranslation();
	const createGroupMutation = useCreateResourceGroupMutation();
	const history = useHistory();
	const queryClient = useQueryClient();
	const toast = useToast();
	const updateEventTypeMutation = useEditEventTypeMutation();
	const resourceGroups = args.resourceGroups.map((group) => {
		return {
			...group,
			isSelected: args.eventType.resourceRules?.availableInGroups?.some((rg) => rg.group.id === group.id),
		};
	});

	const form = useForm({ defaultValues: { resourceGroups } });

	const handleSubmit = form.handleSubmit(async (values) => {
		const availableInGroups = values.resourceGroups
			.filter((rg) => rg.isSelected)
			.map((rg) => ({ group: { id: rg.id }, min: 1 }));

		await updateEventTypeMutation.mutateAsync({
			...args.eventType,
			resourceRules: { ...args.eventType.resourceRules, availableInGroups },
		});

		// The list of resources is updated in the background, so we need to invalidate the query
		queryClient.invalidateQueries({ queryKey: ['resource-groups', 'details', args.eventType.id] });
		// The availability of the service has changed since we have a new resource
		queryClient.invalidateQueries({ queryKey: ['availability'] });
		// We need to update the preview too
		previewService.updatePreview();

		toast({
			isClosable: true,
			status: 'success',
			title: t('Success'),
		});

		args.close();
	});

	function handleCreateGroupClick() {
		const id = uuidService.v4();
		return createGroupMutation
			.mutateAsync({
				name: t('New group'),
				id,
				resources: [],
			})
			.then(() => {
				toast({
					isClosable: true,
					status: 'success',
					title: t('Success'),
				});
				form.reset();
				history.push(ROUTE_AVAILABILITY_GROUPS_DETAILS.replace(':id', id));
			});
	}

	return {
		handleCreateGroupClick,
		form,
		handleSubmit,
		t,
	};
}
